/**
 * WebView — web port of `apps/WunderGuide/common/WebView.tsx`.
 *
 * Renders an iframe with a transparent background by default so embedded
 * pages blend with the glass overlays. Mirrors the RN component's
 * `source: { uri } | { html }` API. External URI loads use a sandbox
 * compatible with most provider checkout flows; inline HTML is wrapped
 * with a transparent body and CSP that disallows top-frame nav.
 *
 * Note: full transparency only takes effect when the embedded page
 * itself does not paint an opaque background. We inject the right
 * styles for inline HTML; for cross-origin URIs we can only set the
 * iframe element's background — many providers will still paint white.
 */

import { forwardRef, useMemo, type CSSProperties } from 'react';

export interface WebViewSource {
  uri?: string;
  html?: string;
  /** Optional <base href> for relative links inside inline HTML. */
  baseUrl?: string;
}

export interface WebViewProps {
  source: WebViewSource;
  /** Default true — paints `background: transparent` on the iframe. */
  transparent?: boolean;
  /** Height/width passed straight to the iframe. */
  style?: CSSProperties;
  className?: string;
  title?: string;
  /**
   * Sandbox flags. Defaults are enough for most provider booking flows.
   * Pass `null` to disable sandboxing entirely (rarely needed).
   */
  sandbox?: string | null;
  /** Permissions header (camera/microphone/etc.) for the embed. */
  allow?: string;
  /** Fired when iframe finishes loading (best-effort). */
  onLoad?: () => void;
  /** Fired on iframe load error (best-effort — many cross-origin loads don't fire). */
  onError?: () => void;
}

const DEFAULT_SANDBOX =
  'allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation-by-user-activation';

function wrapTransparentHtml(html: string, baseUrl?: string): string {
  const baseTag = baseUrl ? `<base href="${baseUrl}">` : '';
  // Theme-matched scrollbars inside the embed so they don't paint as
  // chrome-grey on top of our glass surfaces. Larger thumb on desktop,
  // matching the host page.
  const themeCss = `
    html,body{background:transparent !important;margin:0;padding:0;color:inherit;font-family:inherit;}
    *::-webkit-scrollbar{width:8px;height:8px;}
    *::-webkit-scrollbar-track{background:rgba(28,8,50,0.35);border-radius:999px;}
    *::-webkit-scrollbar-thumb{background:linear-gradient(180deg,#5a1da3,#aa12ff);border-radius:999px;border:1px solid rgba(255,255,255,0.06);}
    *::-webkit-scrollbar-thumb:hover{background:linear-gradient(180deg,#6b27c0,#c235ff);}
    *{scrollbar-color:#aa12ff rgba(28,8,50,0.35);scrollbar-width:thin;}
    @media (min-width: 900px){*::-webkit-scrollbar{width:12px;height:12px;} *{scrollbar-width:auto;}}
  `;
  return `<!doctype html><html><head><meta charset="utf-8">${baseTag}<style>${themeCss}</style></head><body>${html}</body></html>`;
}

const WebView = forwardRef<HTMLIFrameElement, WebViewProps>(function WebView(
  {
    source,
    transparent = true,
    style,
    className,
    title = 'Embedded content',
    sandbox = DEFAULT_SANDBOX,
    allow,
    onLoad,
    onError,
  },
  ref,
) {
  const srcDoc = useMemo(
    () =>
      source.html
        ? wrapTransparentHtml(source.html, source.baseUrl)
        : undefined,
    [source.html, source.baseUrl],
  );

  const baseStyle: CSSProperties = {
    border: 0,
    width: '100%',
    height: '100%',
    background: transparent ? 'transparent' : '#fff',
    colorScheme: transparent ? 'normal' : undefined,
    ...style,
  };

  return (
    <iframe
      ref={ref}
      title={title}
      className={className}
      style={baseStyle}
      src={source.uri}
      srcDoc={srcDoc}
      sandbox={sandbox ?? undefined}
      allow={allow}
      onLoad={onLoad}
      onError={onError}
    />
  );
});

export default WebView;
